import api from './api';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Format all numbers using only Lakh (L) terminology for consistency
 * @param {number} num - The number to format
 * @returns {string} - Formatted number with L suffix
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  
  // Always convert to Lakhs (1 Lakh = 100,000)
  const inLakhs = num / 100000;
  
  // Format based on the value in Lakhs
  if (Math.abs(inLakhs) >= 1000) {
    // Very large values (1000+ L): show with 0 decimal places
    return inLakhs.toFixed(0) + 'L';
  } else if (Math.abs(inLakhs) >= 100) {
    // Large values (100-999 L): show with 1 decimal place
    return inLakhs.toFixed(1) + 'L';
  } else if (Math.abs(inLakhs) >= 10) {
    // Medium values (10-99 L): show with 1 decimal place
    return inLakhs.toFixed(1) + 'L';
  } else if (Math.abs(inLakhs) >= 1) {
    // Smaller values (1-9 L): show with 2 decimal places
    return inLakhs.toFixed(2) + 'L';
  } else {
    // Very small values (< 1 L): show with 2 decimal places
    return inLakhs.toFixed(2) + 'L';
  }
};

/**
 * Format moneyness percentage for display
 * @param {number} moneynessPercent - The moneyness percentage value (e.g., 0.23, -0.17)
 * @returns {string} - Formatted string with sign and % symbol (e.g., "+0.23%", "-0.17%")
 */
export const formatMoneyness = (moneynessPercent) => {
  if (moneynessPercent === null || moneynessPercent === undefined) return '';
  
  const value = Number(moneynessPercent);
  if (isNaN(value)) return '';
  
  // Format with 2 decimal places and add + sign for positive values
  const formatted = value.toFixed(2);
  return value > 0 ? `+${formatted}%` : `${formatted}%`;
};

/**
 * Get color class based on moneyness value
 * @param {number} moneynessPercent - The moneyness percentage value
 * @returns {string} - CSS class for color styling
 */
export const getMoneynessColorClass = (moneynessPercent) => {
  if (moneynessPercent === null || moneynessPercent === undefined) return '';
  
  const absValue = Math.abs(moneynessPercent);
  
  // ATM range (within ±0.15%)
  if (absValue < 0.15) {
    return 'moneyness-atm'; // Yellow/amber color
  }
  
  // OTM/ITM ranges
  if (moneynessPercent > 0) {
    return 'moneyness-positive'; // Green (strike below forward)
  } else {
    return 'moneyness-negative'; // Red (strike above forward)
  }
};

export const fetchOptionChainData = async (index, expiry, strikes = 20) => {
  
  // Validate input parameters
  if (!index || !expiry) {
    console.error('Missing required parameters for option chain fetch');
    throw new Error('Missing required parameters');
  }
  
  // Ensure strikes is a valid number and convert to string
  const strikesValue = parseInt(strikes, 10);
  const validStrikes = !isNaN(strikesValue) && strikesValue > 0 ? strikesValue : 20;
  
  const params = new URLSearchParams({
    index,
    expiry,
    strikes: validStrikes.toString()
  });

  let response;
  
  try {
    response = await api.get(`${API_ENDPOINTS.OPTION_CHAIN}?${params}`);
  } catch (error) {
    console.error('Option chain API error:', error.message);
    // If there's a specific error with strikes parameter, try with default value
    if (validStrikes !== 20) {
      const fallbackParams = new URLSearchParams({
        index,
        expiry,
        strikes: '20'
      });
      try {
        response = await api.get(`${API_ENDPOINTS.OPTION_CHAIN}?${fallbackParams}`);
      } catch (fallbackError) {
        console.error('Fallback option chain API error:', fallbackError.message);
        throw fallbackError;
      }
    } else {
      throw error;
    }
  }
  
  // Process the data to format OI, Volume, and Moneyness values
  if (response.data && response.data.success && response.data.data && response.data.data.chain) {
    response.data.data.chain = response.data.data.chain.map(item => ({
      ...item,
      // Format moneyness for display
      formatted_moneyness: formatMoneyness(item.moneyness_percent),
      moneyness_color_class: getMoneynessColorClass(item.moneyness_percent),
      CE: item.CE ? {
        ...item.CE,
        formatted_oi: formatNumber(item.CE.oi),
        formatted_volume: formatNumber(item.CE.volume)
      } : null,
      PE: item.PE ? {
        ...item.PE,
        formatted_oi: formatNumber(item.PE.oi),
        formatted_volume: formatNumber(item.PE.volume)
      } : null
    }));
  }
  
  return response.data;
};

// Order placement has been moved to orderService.js
export const placeOrder = async (orderData) => {
  // Import here to avoid circular dependencies
  const { placeOrder: placeOrderService } = await import('./orderService');
  return await placeOrderService(orderData);
};

/**
 * Fetch dummy positions data to generate Redis commands
 * @param {string} index - Index name (e.g., "NIFTY", "BANKNIFTY")
 * @param {string} expiry - Expiry date in YYYY-MM-DD format
 * @returns {Promise<Object>} - API response with Redis commands
 */
export const fetchDummyPositions = async (index, expiry) => {
  if (!index || !expiry) {
    throw new Error('index and expiry are required parameters');
  }

  const params = new URLSearchParams({
    index,
    expiry
  });

  try {
    // Using port 8080 as per the API configuration
    // Cookies sent automatically with api.get (withCredentials: true in api.js)
    const response = await api.get(`${API_ENDPOINTS.DUMMY_POSITIONS}?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dummy positions:', error.message);
    throw error;
  }
};